import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Flex, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import {
  StTimelineActivityCardContainer,
  StTimelineActivityCardContent,
  StTimelineActivityCardDescription,
} from './TimelineActivityCard.styled'

import type { FC, ReactNode } from 'react'
import type { ActivityCardDescriptionProps } from '@/modules/shared/components/activity-card/ActivityCard.types'
import type { TimelineActivity } from '@/modules/timeline/components/timeline/Timeline'
import type { PartialRecord } from '@/utils/typescript'

const cardIcons: PartialRecord<TimelineActivity['type'], ReactNode> = {
  Appointment: <FontAwesomeIcon icon={['fal', 'calendar-day']} />,
  Intake: <FontAwesomeIcon icon={['fal', 'house-medical']} />,
}

const TimelineActivityCard: FC<TimelineActivity> = (props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const location = useLocation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const {
    id,
    type,
    completed,
    description,
    scheduledTime,
    showStart,
    waitTime,
    isActive = true,
    hasPassed,
    hasExpired,
    onClick,
  } = props

  const cardIcon = useMemo(() => cardIcons[type], [type])
  const canShowTime = useMemo(() => {
    return showStart && type !== 'Task'
  }, [showStart, type])

  const isMessageOrBrochure = type === 'Message' || type === 'Brochure'
  const isBrochure = type === 'Brochure'

  const itemIsSelected = location.pathname.includes(id.toString())

  return (
    <StTimelineActivityCardContainer hasPassed={hasPassed} selected={itemIsSelected} onClick={onClick}>
      <div id={`card_${id}`} style={{ position: 'absolute', top: '-60px' }} />
      <FlexRow alignItems='center' style={{ width: '100%' }}>
        {cardIcon}
        <Margin ml={theme.UI.Spacing.Space2} />

        <FlexRow flexGrow={1} alignItems='center' justifyContent='space-between'>
          <H4 color={isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText} noMargin>
            {i18n._(activityTypeTranslations[type] as string)}
          </H4>
          {scheduledTime && canShowTime ? (
            <Span type='BodyLargeBold'>
              {formatLocale(scheduledTime, 'HH:mm')}
              {waitTime ? (
                <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
                  {waitTime}
                </Span>
              ) : null}
            </Span>
          ) : null}
        </FlexRow>
      </FlexRow>

      {description ? (
        <>
          <StTimelineActivityCardContent>
            {description.map((value: ActivityCardDescriptionProps, index: number) => {
              return (
                <Flex justifyContent='space-between' alignItems='center' key={`${value}_${index}`}>
                  <StTimelineActivityCardDescription
                    type={value.type}
                    color={
                      value.color ?? (isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText)
                    }
                  >
                    {value.text}
                  </StTimelineActivityCardDescription>

                  {index === description.length - 1 &&
                  (isMessageOrBrochure || (!completed && !hasExpired && isActive)) ? (
                    <FontAwesomeIcon icon={['fal', isBrochure ? 'external-link' : 'chevron-right']} fontSize={16} />
                  ) : null}
                </Flex>
              )
            })}
          </StTimelineActivityCardContent>
        </>
      ) : null}
    </StTimelineActivityCardContainer>
  )
}

export { TimelineActivityCard }
